<template>
  <div>
    <el-card v-loading="isLoading" shadow="always">
      <template #header>
        <el-row type="flex" justify="space-between" align="center">
          <el-col :md="8" :sm="8" :xs="10"
            ><el-row type="flex" justify="start">
              <h4>{{ $route.name }}</h4>
            </el-row></el-col
          >
        </el-row>
      </template>
      <el-select v-model="selectedShop">
        <el-option
          v-for="firm in Firms"
          :key="firm.uid"
          :label="firm.name"
          :value="firm.uid"
        ></el-option>
      </el-select>

      <base-input
        v-model="incomingGrowTarget"
        type="money"
        label="Taxa de crescimento anual"
      >
        <template #append> % </template>
      </base-input>
      <base-input
        v-model="taxCorrection"
        label="Taxa de inflação anual"
        type="money"
      >
        <template #append> % </template>
      </base-input>
      <el-row type="flex" align="center" :gutter="4">
        <base-select v-model="selectedMonth" class="my-auto pb-1" size="small">
          <el-option
            v-for="month in MonthOrders"
            :key="month.name"
            :label="month.name"
            :value="month.month"
          ></el-option>
        </base-select>
        <base-select
          v-model="sortField"
          size="small"
          class="flex-1-1"
          placeholder="Ordenar por"
        >
          <el-option
            v-for="field in ORDER_FIELDS"
            :key="field.key"
            :label="field.label"
            :value="field.key"
          >
          </el-option>
        </base-select>
        <base-button
          onlyIcon
          type="secondary"
          icon="el-icon-download"
          flex="flex-fit"
          text="Baixar relatório"
          @click="downloadPDF"
          title="Baixar relatório"
        />
      </el-row>
      <el-divider />
      <div v-if="selectedShop">
        <vue-apex-charts
          :key="chartKey"
          v-if="OrdersSeries?.length"
          :options="getAreaOptions()"
          :series="AllSeries"
          height="500"
        ></vue-apex-charts>
        <el-empty v-else></el-empty>
        <el-descriptions
          v-if="OrdersSeries?.length"
          title="Ticket Médio"
          :column="6"
          border
          direction="vertical"
        >
          <el-descriptions-item
            v-for="(avgOrder, month) in AvgOrders"
            :key="avgOrder.name"
            :label="avgOrder.name"
          >
            <strong
              >Ticket Médio:
              {{ currencyFormatter.format(avgOrder.avg || 0) }}</strong
            >
            <br />
            <strong>Quantidade de Vendas: {{ avgOrder.count }}</strong>
            <br />
            <strong
              >Total de Compras:
              {{ currencyFormatter.format(sumBillsByMonth(month)) }}</strong
            >
            <small
              v-for="costCenter of ['FRIGORIFICO', 'PRODUTOR RURAL', 'REVENDA']"
              :key="costCenter"
            >
              <br />
              {{ costCenter }}:
              {{ currencyFormatter.format(sumBillsByMonth(month, costCenter)) }}
            </small>
            <br />
            <strong
              >Total de Vendas:
              {{
                currencyFormatter.format((avgOrder.avg || 0) * avgOrder.count)
              }}</strong
            >
            <small
              v-for="(
                groupedSells, productCategory
              ) in SellsGroupedByProductCategory"
              :key="productCategory + groupedSells.length"
            >
              <br />
              {{ productCategory }}:
              {{ printAmountAndQuantity(month, groupedSells) }}
            </small>
            <br />
            <strong
              :class="{
                'is-positive':
                  (((avgOrder.avg || 0) * avgOrder.count) /
                    sumBillsByMonth(month) -
                    1) *
                    100 >
                  22,
                'is-negative':
                  (((avgOrder.avg || 0) * avgOrder.count) /
                    sumBillsByMonth(month) -
                    1) *
                    100 <=
                  22,
              }"
              >Resultado %:
              {{
                !!((avgOrder.avg || 0) * avgOrder.count) &&
                sumBillsByMonth(month)
                  ? numberFormatter.format(
                      (((avgOrder.avg || 0) * avgOrder.count) /
                        sumBillsByMonth(month) -
                        1) *
                        100
                    )
                  : "-"
              }}
              %</strong
            >
          </el-descriptions-item>
        </el-descriptions>
      </div>
      <el-empty
        v-else
        description="Selecione um açougue pra iniciar"
      ></el-empty>
    </el-card>

    <filter-orders-modal
      :showModal="showFilterOrdersModal"
      :filters="filterOptions"
      @close-modal="showFilterOrdersModal = false"
      @update-filters="updateFilters"
    ></filter-orders-modal>
  </div>
</template>

<script>
import FilterOrdersModal from "./modals/FilterOrdersModal.vue";
import VueApexCharts from "vue3-apexcharts";
import OrderService from "../services/orders";
import FirmService from "../services/firms";
import SellService from "../services/sells";
import CostService from "../services/costs";
import BaseButton from "../components/BaseButton.vue";
import BaseSelect from "../components/BaseSelect.vue";
import { generatePDF } from "../services/reports";
import { uuid } from "vue-uuid";
import {
  dateFormatter,
  currencyFormatter,
  numberFormatter,
} from "../utils/formatters";
import { ORDER_FIELDS } from "../utils/constants";

export default {
  name: "ShopSellsTrends",
  components: {
    FilterOrdersModal,
    VueApexCharts,
    BaseButton,
    BaseSelect,
  },
  data: () => ({
    showFilterOrdersModal: false,
    ORDER_FIELDS,
    sortField: "0",
    orders: null,
    chartKey: uuid.v4(),
    selectedMonth: null,
    filterOptions: {
      status: ["completed", "paid", "finalized"],
      delivered_at_start: getFirstDayInMonth(),
      delivered_at_end: new Date(),
    },
    isLoadingDownloadButton: false,
    isLoading: false,
    dateFormatter,
    currencyFormatter,
    numberFormatter,
    lastEighteenMonths: [],
    incomingGrowTarget: 20,
    taxCorrection: 5,
    dateMonthFormatter: new Intl.DateTimeFormat("pt-BR", {
      month: "long",
    }),
    selectedShop: null,
    firms: null,
    sells: null,
    sumBillsByCost: null,
  }),
  mounted() {
    this.fetchFirms();
  },
  computed: {
    Firms() {
      return this.firms || [];
    },
    Orders() {
      return this.orders || [];
    },
    AllSeries() {
      return [
        ...this.OrdersSeries,
        //...this.PublicOrdersSeries,
        //...this.PrivateOrdersSeries,
      ];
    },
    SellsGroupedByProductCategory() {
      return (
        this.sells?.reduce((grouped, sell) => {
          const groupedKey =
            sell?.product?.at(0)?.category?.name || "SEM CATEGORIA";
          if (!grouped[groupedKey]) grouped[groupedKey] = [];
          grouped[groupedKey].push(sell);
          return grouped;
        }, {}) || {}
      );
    },
    MonthOrders() {
      return this.AvgOrders.map(({ month, name }) => ({ month, name }));
    },
    AvgOrders() {
      const sumOrders = this.lastEighteenMonths;
      return sumOrders
        .slice(0, 6)
        .map((e, i) => {
          const month = new Date();
          month.setMonth(month.getMonth() - i);
          return {
            name: this.dateMonthFormatter.format(month),
            month: month.getMonth(),
            year: month.getFullYear(),
            sum: e.sum,
            avg: e.sum / e.count,
            count: e.count,
          };
        })
        .reverse();
    },
    OrdersSeries() {
      if (!this.lastEighteenMonths.length) return [];
      const sumOrders = this.lastEighteenMonths;

      const lastSixMonthsMap = [];
      const lastYearMonthsMap = [];
      Array.from({ length: 6 }).forEach((_, i) => {
        const date = new Date();
        date.setMonth(date.getMonth() - i);
        lastSixMonthsMap.push(sumOrders[i].sum);
        date.setMonth(date.getMonth() - 12);
        lastYearMonthsMap.push(sumOrders[i + 12].sum);
      });
      lastSixMonthsMap.reverse();
      lastYearMonthsMap.reverse();

      const lastYearMonthsCorrection = lastYearMonthsMap.map((lastYear) => {
        return (
          lastYear *
          (1 +
            (Number(this.taxCorrection) + Number(this.incomingGrowTarget)) /
              100)
        );
      });

      const lastYearProjection = lastYearMonthsMap.map(
        (lastYear, index, months) => {
          if (index !== months.length - 1) return 0;

          const days = new Date().getDate();
          const sundays =
            Math.floor(days / 7) + new Date().getDay() === 0 ? 1 : 0;
          return (lastYear / 30.4) * (days - sundays);
        }
      );

      return [
        {
          name: "Vendas no mês",
          data: lastSixMonthsMap,
          type: "column",
        },
        {
          name: "Vendas último ano",
          data: lastYearMonthsMap,
          type: "column",
        },
        {
          name: "Meta de vendas",
          data: lastYearMonthsCorrection,
          type: "column",
        },
        {
          name: "Projeção de vendas",
          data: lastYearProjection,
          type: "column",
        },
      ];
    },
  },
  methods: {
    printAmountAndQuantity(month, sells) {
      const { amount, quantity } = this.sumSellsByMonth(month, sells);
      return `${currencyFormatter.format(
        amount
      )} (${this.numberFormatter.format(quantity)} kg)`;
    },
    sumSellsByMonth(month, sells) {
      const avgMonth = this.AvgOrders[month];
      if (!avgMonth) return {};

      return (
        sells?.reduce(
          (total, sell) => {
            const soldIn = new Date(sell.sold_in);
            if (
              soldIn.getMonth() === avgMonth.month &&
              soldIn.getFullYear() === avgMonth.year
            ) {
              total.amount += Number(sell.total_amount) || 0;
              total.quantity += Number(sell.quantity) || 0;
            }

            return total;
          },
          { amount: 0, quantity: 0 }
        ) || {}
      );
    },
    sumBillsByMonth(month, selectedCost) {
      let sum = 0;
      for (const cost in this.sumBillsByCost) {
        if (selectedCost && selectedCost !== cost) continue;

        const avgMonth = this.AvgOrders[month];
        const summedBills = this.sumBillsByCost[cost].find(
          (s) => s.year === avgMonth.year && s.month === 1 + avgMonth.month
        );

        sum += Number(summedBills?.total) || 0;
      }

      return sum || 0;
    },
    async fetchBillsByCost() {
      const costs = ["FRIGORIFICO", "PRODUTOR RURAL", "REVENDA"];
      const firmId = this.selectedShop;
      const costsQueriesThisYear = costs.map((cost) => {
        const queryObj = {
          category: cost,
          year: new Date().getFullYear(),
          dateColumn: "expires_at",
          amountColumn: "amount",
          firm_id: firmId,
        };

        return CostService().sum(queryObj);
      });

      const costsQueriesPreviousYear = costs.map((cost) => {
        const queryObj = {
          category: cost,
          year: new Date().getFullYear() - 1,
          dateColumn: "expires_at",
          amountColumn: "amount",
          firm_id: firmId,
        };

        return CostService().sum(queryObj);
      });

      const costsLastYear = (await Promise.all(costsQueriesPreviousYear)).map(
        ({ costs }) => costs
      );
      const costsThisYear = (await Promise.all(costsQueriesThisYear)).map(
        ({ costs }) => costs
      );
      const billsByCost = costs.reduce((totals, cost, index) => {
        totals[cost] = [...costsLastYear[index], ...costsThisYear[index]];
        return totals;
      }, {});
      this.sumBillsByCost = billsByCost;
    },
    async fetchFirms() {
      const { firms } = await FirmService().index({ type: "shop" });
      if (firms) this.firms = firms;
    },
    getMonthName(date) {
      return new Intl.DateTimeFormat("pt-BR", { month: "long" }).format(date);
    },
    async fetchSum(filters) {
      const { sum } = await OrderService().sum(filters);
      const { count } = await OrderService().count(filters);
      return { sum, count };
    },
    async fetchSellsByFirmId(firmId) {
      const firm = this.firms.find((f) => f.uid === firmId);
      if (!firm) return;

      this.isLoading = true;

      const sold_in_start = new Date();
      sold_in_start.setDate(1);
      const LAST_EIGHTEEN_MONTHS = 18;
      sold_in_start.setMonth(sold_in_start.getMonth() - LAST_EIGHTEEN_MONTHS);

      this.sells = null;

      const { sells } = await SellService().index({
        firm_code: firm.firm_code,
        sold_in_start: sold_in_start.toISOString().slice(0, 10),
      });

      if (sells) this.sells = sells;

      this.isLoading = false;
    },
    async loadLastHalfYearOrders() {
      const lastEighteenMonths = Array.from({ length: 18 }).map((_, i) => {
        const _d = new Date();
        _d.setDate(1);
        _d.setMonth(_d.getMonth() - i);

        return this.sells.filter((sell) => {
          const soldIn = new Date(sell.sold_in);
          return (
            soldIn.getMonth() === _d.getMonth() &&
            soldIn.getFullYear() === _d.getFullYear()
          );
        });
      });

      this.lastEighteenMonths = lastEighteenMonths.map((sellsInMonth) => {
        const totalSum =
          sellsInMonth?.reduce(
            (totalSold, sell) => totalSold + Number(sell.total_amount),
            0
          ) || 0;
        const totalCount = sellsInMonth?.length || 0;
        return {
          sum: Number(totalSum) || 0,
          count: Number(totalCount) || 0,
        };
      });
    },
    rebuildChart() {
      this.chartKey = uuid.v4();
    },
    isPublicSell(order) {
      return order?.client?.legal?.social_name?.includes("PREGAO");
    },
    getAreaOptions() {
      return {
        chart: {
          type: "bar",
          zoom: {
            enabled: false,
          },
          locales: [
            {
              name: "pt-br",
              options: {
                months: [
                  "Janeiro",
                  "Fevereiro",
                  "Março",
                  "Abril",
                  "Maio",
                  "Junho",
                  "Julho",
                  "Agosto",
                  "Setembro",
                  "Outubro",
                  "Novembro",
                  "Dezembro",
                ],
                shortMonths: [
                  "Jan",
                  "Fev",
                  "Mar",
                  "Abr",
                  "Mai",
                  "Jun",
                  "Jul",
                  "Ago",
                  "Set",
                  "Out",
                  "Nov",
                  "Dez",
                ],
                days: [
                  "Domingo",
                  "Segunda",
                  "Terça",
                  "Quarta",
                  "Quinta",
                  "Sexta",
                  "Sábado",
                ],
                shortDays: ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sab"],
                toolbar: {
                  exportToSVG: "Baixar SVG",
                  exportToPNG: "Baixar PNG",
                  exportToCSV: "Baixar CSV",
                  menu: "Menu",
                  selection: "Selecionar",
                  selectionZoom: "Selecionar Zoom",
                  zoomIn: "Aumentar",
                  zoomOut: "Diminuir",
                  pan: "Navegação",
                  reset: "Reiniciar Zoom",
                },
              },
            },
          ],
          defaultLocale: "pt-br",
        },
        //colors: ["#0d84ff", "#ff8c00", "#ff000f"],
        dataLabels: {
          enabled: false,
        },
        theme: {
          mode: "light",
          palette: "palette0",
        },
        stroke: {
          curve: "smooth",
          width: [0, 0, 0, 0, 4],
        },
        markers: {
          size: [0, 0, 0, 0, 4],
        },
        title: {
          text: "Vendas dos últimos 6 meses comparativo com último ano",
          align: "left",
        },
        xaxis: {
          categories: Array.from({ length: 6 })
            .map((_, i) =>
              this.getMonthName(
                new Date(new Date().setMonth(new Date().getMonth() - i))
              )
            )
            .reverse(),
        },
        yaxis: [
          {
            labels: {
              formatter: (v) => this.currencyFormatter.format(Number(v) || 0),
            },
            title: {
              text: "Vendas",
            },
          },
        ],
      };
    },
    sumTotalSold(index) {
      const _ = this.AllSeries[index];

      if (_) return _?.data?.reduce((t, e) => (t += e[1]), 0);
      else return 0;
    },
    changeDownloadButtonStatus() {
      this.isLoadingDownloadButton = !this.isLoadingDownloadButton;
    },
    openFilterCashierModal() {
      this.showFilterOrdersModal = true;
    },
    updateFilters(filters) {
      this.filterOptions = filters || {};
      this.fetchOrders();
    },
    groupSellsByProductCategory(sells) {
      if (!sells) {
        return {};
      }

      return Object.values(sells).reduce((total, sell) => {
        const firstSell = sell[0];
        const key =
          firstSell?.product?.at(0)?.category?.name || "SEM CATEGORIA";
        if (total[key]) {
          total[key].push(sell);
        } else {
          total[key] = [sell];
        }

        return total;
      }, {});
    },
    downloadPDF() {
      const monthlySells = this.groupSellsByProductCategory(
        this.generateSellsByMonth()
      );
      const self = this;
      const headers = [
        [
          "produto",
          "total de vendas",
          "peso vendido (kg)",
          "valor médio (R$)",
          "total de vendas (R$)",
        ],
      ];
      let content = Object.entries(monthlySells).map(([, sells]) =>
        sells.map((sell) => {
          const total = [];
          const totalQuantity = sell.reduce(
            (t, sell) => t + Number(sell.quantity),
            0
          );
          const totalAmount = sell.reduce(
            (t, sell) => t + Number(sell.total_amount),
            0
          );
          const productName = sell?.at(0).product_name;
          total.push(productName);
          total.push(sell.length);
          total.push(totalQuantity);
          total.push(totalAmount / totalQuantity);
          total.push(totalAmount);

          return total;
        })
      );
      content = content.map((sells) =>
        sells
          .sort((a, b) =>
            a[Number(self.sortField)] > b[Number(self.sortField)] ? -1 : 1
          )
          .map(([name, totalSells, totalQuantity, avgAmount, totalAmount]) => [
            name,
            totalSells,
            numberFormatter.format(totalQuantity),
            currencyFormatter.format(avgAmount),
            currencyFormatter.format(totalAmount),
          ])
      );
      const headerMessage = "Relatório de vendas do açougue";

      generatePDF({
        fileName: `Relatório de vendas dos açougues`,
        headers,
        content,
        headerMessage,
        isMultipleTables: true,
        addTableTitle: true,
        tableMessages: Object.keys(monthlySells),
      });
    },
    generateSellsByMonth() {
      const avgMonth = this.AvgOrders.find(
        (avgOrder) => avgOrder.month === this.selectedMonth
      );
      const monthSells = this.getSellsByMonth(avgMonth.month, avgMonth.year);
      return monthSells.reduce((total, sell) => {
        const sellKey = sell.product_name;
        if (total[sellKey]) {
          total[sellKey].push(sell);
        } else {
          total[sellKey] = [sell];
        }

        return total;
      }, {});
    },
    getSellsByMonth(month, year) {
      return this.sells?.filter((sell) => {
        const soldIn = new Date(sell.sold_in);
        const selectedShopId = this.selectedShop;
        const selectedShop = this.Firms.find(
          (firm) => firm.uid === selectedShopId
        );
        return (
          soldIn.getMonth() === month &&
          soldIn.getFullYear() === year &&
          sell.firm_code === selectedShop.firm_code
        );
      });
    },
    calculateResult(r) {
      return Number(Number(r.total_cashier) - Number(r.check_total)) || 0;
    },
    orderByFirms() {
      return {
        "COMERCIO DE CARNES DONAU EIRELI": JSON.parse(
          JSON.stringify(this.Orders)
        ),
      };
    },
    hasValidNumber(e) {
      return !isNaN(Number(e));
    },
  },
  watch: {
    async selectedShop(v) {
      await this.fetchSellsByFirmId(v);
      this.loadLastHalfYearOrders();
      this.fetchBillsByCost();
    },
  },
};
const getFirstDayInMonth = () => {
  const _ = new Date();
  return new Date(_.getFullYear(), _.getMonth(), 1);
};
</script>
<style scoped>
.el-card {
  width: 100%;
}

tr.unavailable {
  background-color: #fffde7;
}

.is-negative {
  color: red !important;
}

.is-positive {
  color: blue !important;
}

tr.problem {
  background-color: #fbe9e7;
}

.mb-0 {
  margin-bottom: 0px !important;
}

.summary {
  color: #909399;
  font-weight: 700 !important;
  font-family: "Helvetica Neue", Helvetica, "PingFang SC", "Hiragino Sans GB",
    "Microsoft YaHei", SimSun, sans-serif;
  font-size: 12px;
}

.mt-1 {
  margin-top: 10px !important;
}

.my-2 {
  margin-bottom: 7px !important;
}

.summary-black {
  color: #333 !important;
}

.flex-max {
  flex-basis: max-content !important;
}

.flex-1-1 {
  flex: 1 1 auto;
}

.flex-fit {
  flex-basis: content;
}

.my-auto {
  margin-top: auto !important;
  margin-bottom: auto !important;
}

.pb-1 {
  padding-bottom: 5px;
}
</style>
